import React from "react";
import { Play, Heart, Star, Users, Download, Clock, Share2, ShieldCheck, Film } from "lucide-react";
import { useFavorites } from "@/context/FavoritesContext";
import { toast } from "sonner";

const DIFF = { easy: "Easy", medium: "Medium", hard: "Hard", expert: "Expert" };

export function PlayCard({ game, onPlay }) {
  const { isFav, toggle } = useFavorites();
  const fav = isFav("game", game.id);
  const w = game.world || {};

  const share = async (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/g/${game.id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied! 🔗", { description: url }); } catch { toast(url); }
  };

  return (
    <div data-testid={`play-card-${game.id}`} className="group rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden card-hover flex flex-col">
      <div onClick={() => onPlay?.(game)} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter") onPlay?.(game); }}
        className="relative h-32 grid place-items-center overflow-hidden text-left w-full cursor-pointer"
        style={{ background: `linear-gradient(135deg, ${w.bgColor || "#0b1026"}, ${w.accent || "#1f6feb"})` }}>
        {game.cover_image
          ? <img src={`${process.env.REACT_APP_BACKEND_URL}${game.cover_image}`} alt={game.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
          : <span className="text-5xl group-hover:scale-110 transition-transform">{game.cover_emoji || "🎮"}</span>}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1 max-w-[75%]">
          {game.multiplayer && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/90 text-white">MULTIPLAYER</span>}
          {game.robot_certified && <span title={`Robot-certified fun — scored ${game.robot_best}/100`} data-testid={`certified-badge-${game.id}`} className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-500/90 text-white">🤖 {game.robot_best}</span>}
          {game.has_premium
            ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/90 text-white">${game.price}</span>
            : <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/50 text-white backdrop-blur">FREE</span>}
        </div>
        {game.licence?.verified && (
          <span title="Verified original — no protected IP detected" data-testid={`verified-badge-${game.id}`}
            className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/55 text-emerald-300 backdrop-blur">
            <ShieldCheck className="w-3 h-3" /> Verified
          </span>
        )}
        {game.hot_clips && (
          <span title="Hot player clips — trending gameplay" data-testid={`hot-clips-${game.id}`}
            className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-fuchsia-500/80 text-white backdrop-blur">
            <Film className="w-3 h-3" /> Hot clips
          </span>
        )}
        <button onClick={(e) => { e.stopPropagation(); toggle("game", game.id); }} data-testid={`fav-btn-${game.id}`}
          className="absolute top-2 right-2 w-8 h-8 grid place-items-center rounded-full bg-black/40 backdrop-blur hover:bg-black/60 transition-colors">
          <Heart className={`w-4 h-4 ${fav ? "fill-rose-500 text-rose-500" : "text-white"}`} />
        </button>
      </div>
      <div className="p-3.5 flex-1 flex flex-col">
        <div className="font-bold truncate">{game.title}</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">by {game.owner_name}{game.owner_verified && <ShieldCheck className="inline w-3 h-3 text-emerald-500 ml-1 -mt-0.5" aria-label="Verified Creator" />} · {game.category}</div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-[hsl(var(--muted-foreground))]">
          {game.rating > 0 && <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {game.rating}</span>}
          <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {game.plays || 0}</span>
          {game.downloads > 0 && <span className="flex items-center gap-1"><Download className="w-3 h-3" /> {game.downloads}</span>}
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {game.est_minutes}m</span>
          <span className="capitalize">{DIFF[game.difficulty] || game.difficulty}</span>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={() => onPlay?.(game)} data-testid={`play-now-${game.id}`}
            className="flex-1 px-3 py-2 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-bold flex items-center justify-center gap-1.5 hover:opacity-90 transition-opacity">
            <Play className="w-4 h-4" fill="white" /> Play Now
          </button>
          <button onClick={share} data-testid={`share-btn-${game.id}`}
            className="w-9 h-9 grid place-items-center rounded-full bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--border))] transition-colors">
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function CardRow({ title, games, onPlay, testid }) {
  if (!games || games.length === 0) return null;
  return (
    <div className="mb-8" data-testid={testid}>
      <h2 className="font-display text-xl font-bold mb-3">{title}</h2>
      <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {games.map((g) => (
          <div key={g.id} className="w-56 shrink-0 snap-start"><PlayCard game={g} onPlay={onPlay} /></div>
        ))}
      </div>
    </div>
  );
}
